import { Switch, VStack } from '@chakra-ui/react';
import { useMemo } from 'react';
import { SettingsCard, SettingsRow } from '../../../../components/settings/primitives';
import SearchableSingleSelect from '../../../../components/SearchableSingleSelect';
import VendorNexAliasMappingsSection from '../../../../components/settings/VendorNexAliasMappingsSection';
import type { SupplierVendorNexAliasGroup } from '../../../../hooks/useGetSettings';
import { cleanVendorNexAliasMappings } from '../../../../utils/vendorNexAliasMappingsUtils';
import {
    NEXSYIS_INVOICE_REFERENCE_OPTIONS,
    NEX_LINE_QUANTITY_BASIS_OPTIONS,
    UNMATCHED_LINE_ITEMS_NEX_SUBMIT_OPTIONS,
    useInvoicingDraft,
} from './shared';
import type { InvoicingSectionProps } from './types';

/** One width for every dropdown on the page, so their edges line up. */
const SELECT_WIDTH = '280px';

const InvoicingGeneralSection = ({ data }: InvoicingSectionProps) => {
    const { invoicing, updateInvoicing, isSaving } = useInvoicingDraft(data);

    const mappings = useMemo(
        () => cleanVendorNexAliasMappings(invoicing.vendorNexAliasMappings ?? []),
        [invoicing.vendorNexAliasMappings],
    );

    const handleMappingsChange = (next: SupplierVendorNexAliasGroup[]) => {
        updateInvoicing({ vendorNexAliasMappings: cleanVendorNexAliasMappings(next) });
    };

    return (
        <VStack spacing={6} align="stretch" pb={16}>
            <SettingsCard
                title="Nexsyis reconciliation"
                description="How invoices are matched to Nexsyis purchase orders."
            >
                <SettingsRow
                    label="Unmatched line items on Nexsyis submit"
                    description="Submit invoices with manual lines from the images when there is no corresponding entry in Nexsyis (e.g. CORE). At least one billable line must be matched."
                    control={
                        <SearchableSingleSelect
                            options={[...UNMATCHED_LINE_ITEMS_NEX_SUBMIT_OPTIONS]}
                            value={invoicing.unmatchedLineItemsNexSubmitMode ?? 'dont_allow'}
                            onChange={(value) =>
                                updateInvoicing({
                                    unmatchedLineItemsNexSubmitMode: value as
                                        | 'dont_allow'
                                        | 'allow_manual'
                                        | 'allow_manual_and_automatic',
                                })
                            }
                            width={SELECT_WIDTH}
                        />
                    }
                />
                <SettingsRow
                    label="Nexsyis invoice reference"
                    description="Choose what type of identifier vendors use to reconcile invoices with Nexsyis (typically a 6-digit ID)."
                    control={
                        <SearchableSingleSelect
                            options={[...NEXSYIS_INVOICE_REFERENCE_OPTIONS]}
                            value={invoicing.nexReconciliationIdentifierMode ?? 'ro'}
                            onChange={(value) =>
                                updateInvoicing({
                                    nexReconciliationIdentifierMode: value as 'ro' | 'po' | 'both',
                                })
                            }
                            width={SELECT_WIDTH}
                        />
                    }
                />
                <SettingsRow
                    label="Compare quantities to Nexsyis"
                    description="Determines which quantity we use to match lines: parts received or parts ordered. Does not apply to return invoices."
                    control={
                        <SearchableSingleSelect
                            options={[...NEX_LINE_QUANTITY_BASIS_OPTIONS]}
                            value={invoicing.nexLineQuantityBasis ?? 'qtyRec'}
                            onChange={(value) =>
                                updateInvoicing({
                                    nexLineQuantityBasis: value as 'qtyRec' | 'qtyOnOrder',
                                })
                            }
                            width={SELECT_WIDTH}
                        />
                    }
                />
            </SettingsCard>

            <SettingsCard title="Line items" description="How costs are sent to the AP invoice.">
                <SettingsRow
                    label="Freight as line item"
                    description="By default, freight is not sent as a line item on invoices. Enable this to submit freight as a line item with the description “Freight”."
                    control={
                        <Switch
                            size="lg"
                            isChecked={invoicing.freightAsLineItem}
                            onChange={(e) => updateInvoicing({ freightAsLineItem: e.target.checked })}
                            colorScheme="partbay-blue"
                            isDisabled={isSaving}
                        />
                    }
                />
                <SettingsRow
                    label="Sales tax as line item"
                    description="By default, taxes are not sent as a line item on invoices. Enable this to submit taxes as a line item with the description “Sales Tax”."
                    control={
                        <Switch
                            size="lg"
                            isChecked={invoicing.salesTaxAsLineItem === true}
                            onChange={(e) => updateInvoicing({ salesTaxAsLineItem: e.target.checked })}
                            colorScheme="partbay-blue"
                            isDisabled={isSaving}
                        />
                    }
                />
            </SettingsCard>

            {/* Renders its own card: it owns a title, an Add button and a table. */}
            <VendorNexAliasMappingsSection
                mappings={mappings}
                onMappingsChange={handleMappingsChange}
                isSaving={isSaving}
            />
        </VStack>
    );
};

export default InvoicingGeneralSection;
